// @oekaki/protocol — wire-level constants shared by client and server.
//
// Server-only configuration (rate limits, snapshot timers, etc.) lives in
// `@oekaki/server` and intentionally does NOT belong here.
// See `docs/_wip/protocol-package.md` §2.7 / §3.
/**
 * WebSocket subprotocol name negotiated via `Sec-WebSocket-Protocol`.
 * (protocol.md §7.2)
 */
export const SUBPROTOCOL_NAME = 'oekaki-v1';
/**
 * Maximum number of characters allowed in `displayName`.
 *
 * Enforced on both the `room.join` and `member.name-change` paths.
 *
 * (protocol-events.md §2.2 / §5.4 — 「1〜32文字程度」)
 * TODO(Phase 7): nail down allowed character ranges (emoji, control chars).
 * (要検証: protocol-events.md §2.11 / §5.7)
 */
export const MAX_DISPLAY_NAME_LENGTH = 32;
export const MIN_DISPLAY_NAME_LENGTH = 1;
/**
 * 入室「あいことば」(entry password) の長さ制約 (文字数).
 *
 * `room.join.auth.entryPassword` (visitor) と dispatcher の
 * `PATCH /admin/rooms/:id { entryPassword }` の両方で同じ制約を使う
 * (owner-vs-entry-password-2026-06-13.md — 入室側の鍵は「あいことば」、
 * 所有側の「オーナーパスワード」(min 8) とは別物なので下限を揃えない:
 * 友達内で口頭共有する短い合言葉を想定し 1 文字から許す).
 */
export const MAX_ENTRY_PASSWORD_LENGTH = 64;
export const MIN_ENTRY_PASSWORD_LENGTH = 1;
/**
 * 入室「あいことば」の正規化 (2026-09-19、entry-password-plaintext).
 *
 * UI を平文入力 (type="text") にして日本語の合言葉を主用途にする方針に
 * 伴い追加。**設定時 (hash 生成直前) と照合時 (verify 直前) の両方**で必ず
 * この関数を通してから使うこと — 本番に hash が 1 つもできていないうちに
 * 正規化を入れておかないと、後から入れると既存 hash と食い違う。
 *
 * - `NFKC` 正規化: 全角/半角英数字・半角/全角カナの揺れを吸収する
 *   (例: "ｱｲｳ１２３" → "アイウ123")。
 * - `trim()`: 前後の空白 (全角スペースも NFKC で半角に変換された後なので
 *   trim で落ちる) を落とす。日本語入力でありがちな余分な空白の混入を吸収。
 *
 * 全角スペースだけの入力は正規化後に空文字列になる — 呼び出し側 (作成 /
 * 設定変更) はこれを「未設定」に丸めず 400 で reject すること (「あいことばを
 * 設定したつもりが実は空」という事故を防ぐ)。
 *
 * べき等 (2 回適用しても結果は同じ) なので、複数箇所で重ねて呼んでも安全。
 */
export const normalizeEntryPassword = (raw) => raw.normalize('NFKC').trim();
/**
 * Maximum length of a single chat message in characters.
 *
 * (protocol-events.md §3.2 — 「1〜500 文字 (要検証)」)
 * TODO(Phase 7): confirm against legacy behaviour and rate-limit policy.
 * (要検証: protocol-events.md §3.5)
 */
export const MAX_CHAT_TEXT_LENGTH = 500;
export const MIN_CHAT_TEXT_LENGTH = 1;
/**
 * Maximum stroke pen width.
 *
 * (protocol.md §4 / protocol-package.md §2.3)
 */
export const MAX_STROKE_WIDTH = 64;
export const MIN_STROKE_WIDTH = 1;
/**
 * Number of fixed layers in the Classic layout (W3, consult 2026-05-26).
 *
 * Classic ships **3 fixed layers** (下から `背` / `中` / `前`, internal ids
 * 0 / 1 / 2). No reorder, no add/remove — that depth is reserved for the
 * β Modern Layout (5–10 layers + opacity / blend / reorder).
 *
 * Lives in `@oekaki/protocol` because both the browser drawing engine
 * (`@oekaki/web` allocates an `EngineCore` with this `layerCount`) and the
 * server snapshot replay (`@oekaki/server` builds `meta.layers` of this
 * length) must agree on the count; protocol is the shared leaf both depend
 * on. (`docs/_wip/w3-classic-layers.md` §2.1)
 */
export const CLASSIC_LAYER_COUNT = 3;
/**
 * Upper bound on `items` in one `whiteboard.region-request` / `whiteboard.region`
 * (= レイヤー数の上限。Modern は 5〜10 枚を想定、docs/_wip/region-move-2026-10-15.md).
 * The server additionally requires `layerId < room layer count`.
 */
export const MAX_REGION_ITEMS = 10;
/**
 * Hard cap on points carried by a single `stroke.segment` event.
 *
 * Clients are expected to flush per `requestAnimationFrame` (~16.7ms),
 * which yields a handful of points per segment. The cap exists to
 * prevent abusive payloads — in practice the value is well above the
 * organic ceiling.
 *
 * (protocol.md §4.4 — 「実用 rAF tick あたり 1〜数点」)
 * TODO(Phase 5+): tune from 20-user load test.
 * (要検証: protocol.md §8 — rAF tick 単位の segment 送信頻度)
 */
export const MAX_SEGMENT_POINTS = 256;
/**
 * Maximum members allowed per room.
 *
 * Typical rooms are 10–20 users; hot rooms can be up to several hundred.
 * (architecture.md §1 — 「1ルームあたり: 10〜20人 (典型) / 最大数百人」)
 * TODO(Phase 6): align with admin / cleanup policy.
 * (要検証)
 */
export const MAX_ROOM_MEMBERS = 500;
/**
 * Lifetime of a `resumeToken` after WS disconnect, in milliseconds.
 *
 * Used by `room.welcome` → `room.resume`. The server keeps the mapping
 * in-memory and therefore loses it on restart; clients are expected to
 * fall back to a regular `room.join` in that case.
 *
 * 役割: **internal identity 保持窓** — WiFi 切替 / モバイル sleep の数秒〜数分
 * シナリオで uid (= members[] エントリの identity) を維持するための時間.
 * これを超えたら fresh join (新 uid だが displayName は localStorage 復元 +
 * snapshot で canvas 復元、実害最小).
 *
 * UI 上の「メンバーリストから名前が消えるまで」の即時性は別軸の
 * `MEMBER_LEFT_SUPPRESS_MS` (= 5 秒) が担う. 旧版では本定数が両方を兼任して
 * いたため、タブ閉じても 30 分名前が残る挙動になっていた (実機検証で長すぎる
 * との判断、2026-05).
 */
export const RESUME_TOKEN_TTL_MS = 5 * 60 * 1000;
/**
 * Window during which `room.member-left { reason: 'disconnect' }` is
 * **suppressed** after a WS close, in milliseconds.
 *
 * 役割: reload (実測 0.5-3s) や WS auto-reconnect 1st retry (1s) など
 * 短時間 outage で peers の members[] から名前がチラつくのを防ぐ用. 経過後に
 * member-left を broadcast し、resume が間に合えば (= timer 発火前に
 * `cancelPendingMemberLeft`) broadcast を取り消す.
 *
 * > 7s の outage では一旦 member-left → 復帰時 member-joined が出る挙動
 * (= 「離席→復帰」を peers が認識できる、UX 上望ましい). identity は別軸の
 * `RESUME_TOKEN_TTL_MS` (5 分) が窓を取り続ける.
 *
 * (architecture.md §18.0 / protocol-events.md §2.10、Phase 8 Task 8-X
 *  「member-left suppression と resume token TTL の役割分離」)
 */
export const MEMBER_LEFT_SUPPRESS_MS = 5 * 1000;
/**
 * Lifetime of a presigned snapshot meta.json GET URL, in milliseconds.
 *
 * Server emits all layer URLs at the same time so they expire together.
 * (architecture.md §14 — 「presigned URL は server が同時発行、有効期限統一 (60秒目安)」
 *  / protocol-events.md §2.3 — 「meta.json への presigned GET URL (短命: 60s)」)
 */
export const SNAPSHOT_URL_TTL_MS = 60 * 1000;
/**
 * Lifetime of an `imagepaste` presigned PUT token, in milliseconds.
 *
 * (protocol-events.md §4.3.2 — 「5分目安」)
 * TODO(Phase 7): finalise upload policy.
 * (要検証)
 */
export const IMAGEPASTE_PUT_URL_TTL_MS = 5 * 60 * 1000;
/**
 * Maximum size in bytes for a single imagepaste upload.
 *
 * (protocol-events.md §4.5 — 「例: 5MB」)
 * TODO(Phase 7): confirm against legacy version.
 * (要検証)
 */
export const MAX_IMAGEPASTE_BYTES = 5 * 1024 * 1024;
/**
 * Window during which a stroke author may issue `stroke.cancel`,
 * in milliseconds. β Modern Layout feature; not enforced in MVP.
 *
 * (protocol-events.md §1.1 — MagicalDraw 仕様準拠 5秒)
 */
export const STROKE_CANCEL_WINDOW_MS = 5 * 1000;
/**
 * カスタムカーソル (custom-cursor-2026-09-19.md §2.2) の固定サイズ.
 *
 * v1 は 24×24 固定 (HiDPI は表示側で 2x 描画、protocol は関与しない). ペイロード
 * は行優先 RGBA (premultiply しない素の値) の生バイト列を base64 化したもの.
 *
 * `CURSOR_RGBA_BYTES = 24*24*4 = 2304`。**2304 は 3 の倍数**なので base64 化に
 * パディング (`=`) が発生せず、`CURSOR_RGBA_BASE64_LENGTH = 2304/3*4 = 3072`
 * ちょうどになる — `customCursorSchema` はこの性質を使い、長さ 3072 の
 * base64 文字集合 (`A-Za-z0-9+/`) のみを許容することで decode 結果が必ず
 * 2304 byte になることを schema 層で保証する (server 側の decode 後の長さ
 * 検証は defense-in-depth、custom-cursor-2026-09-19.md §2.4).
 */
export const CURSOR_SIZE = 24;
export const CURSOR_RGBA_BYTES = CURSOR_SIZE * CURSOR_SIZE * 4;
export const CURSOR_RGBA_BASE64_LENGTH = (CURSOR_RGBA_BYTES / 3) * 4;
/**
 * Hard byte ceiling for the per-room session buffer.
 *
 * Snapshot generation is normally driven by the 5-second time-based
 * trigger; this hard limit is the safety net for emergency overflow.
 * (architecture.md §15 / protocol-events.md §2.10)
 * TODO(Phase 5): wire into the snapshot dispatcher.
 * (要検証)
 *
 * Wired (2026-09): `RoomRegistry` → `SessionBuffer`'s `onHardLimitExceeded`
 * fires `SnapshotDispatcher.enqueue(roomId, 'hard-limit')` — an emergency
 * snapshot attempt, NOT a reject.
 *
 * restore-seed-meta-race-2026-09-24.md 追加確定 14/19: `Broadcaster` also
 * rejects new drawing content once a room's session buffer crosses a
 * SEPARATE, higher ceiling (the fail-closed policy, 確定方針 2 / 追加確定 7,
 * means a snapshot attempt can now legitimately ABORT instead of always
 * landing — an unbounded buffer risks OOM-killing the whole process, every
 * room sharing it). That reject ceiling is deliberately NOT a protocol
 * constant (追加確定 19) — it must be derived from the EFFECTIVE hard limit
 * actually in force (i.e. after `OEKAKI_SESSION_BUFFER_HARD_LIMIT_BYTES`),
 * which only the server composition root (`presentation/main.ts`) knows.
 * See `main.ts`'s `sessionBufferRejectBytes` computation and
 * `Broadcaster.ts`'s `isBufferCapRejectable` for the reject logic itself.
 */
export const SESSION_BUFFER_HARD_LIMIT_BYTES = 5 * 1024 * 1024;
//# sourceMappingURL=constants.js.map